import { apiClient } from './apiClient.js';
import type { ApiResponse } from '@onboarding-dashboard/shared';
import type {
  MetricsSnapshot,
  Milestone,
  Facility,
  ProductIntel,
  TimeFilterKey,
} from '../pages/mission-control/data/constants.js';

export interface MilestoneConfig {
  id: string;
  key: string;
  label: string;
  target: number;
  weight: number;
  prefix: string | null;
  suffix: string | null;
  format: string;
  manualCurrent: number | null;
  sortOrder: number;
}

export interface FacilityConfig {
  id: string;
  firebaseProject: string;
  facilityDocId: string;
  facilityName: string;
  region: string;
  facilityType: 'commercial' | 'legacy' | 'disabled';
  linkedFacilityId: string | null;
  liveDate?: string | null;
  location?: string | null;
}

export interface ProductIntelItemConfig {
  id: string;
  productConfigId: string;
  label: string;
  value: string;
  sortOrder: number;
}

export interface ProductConfig {
  id: string;
  name: string;
  platform: string;
  sortOrder: number;
  items: ProductIntelItemConfig[];
}

export interface FirebaseProjectStatus {
  status: 'connected' | 'error' | 'not_configured';
  latencyMs?: number;
}

/** Sentinel `facilityId` for the row aggregating every legacy facility. */
export const LEGACY_INQUIRY_GROUP_ID = '__legacy__';

/** One facility's contribution to the Inquiries metric, internal activity included. */
export interface FacilityInquiryCount {
  facilityId: string;
  facilityName: string;
  count: number;
  /**
   * Share of `count` raised by internal `@exex.ai` staff, not deducted from it.
   * Carried on the payload but not shown on the card.
   */
  internalCount: number;
  /**
   * Set only on the aggregated legacy row: the facilities it rolls up, descending
   * by count. Their counts sum to `count`.
   */
  legacyFacilities?: FacilityInquiryCount[];
}

export interface DashboardData {
  /** Commercial facilities only. */
  metrics: Record<TimeFilterKey, MetricsSnapshot>;
  /** Every active facility across all projects — the panel's default scope. */
  metricsWithLegacy: Record<TimeFilterKey, MetricsSnapshot>;
  /** Descending by count; facilities with no inquiries are omitted. */
  inquiriesByFacility: Record<TimeFilterKey, FacilityInquiryCount[]>;
  /** Same commercial rows, then one `LEGACY_INQUIRY_GROUP_ID` roll-up row. */
  inquiriesByFacilityWithLegacy: Record<TimeFilterKey, FacilityInquiryCount[]>;
  milestones: Milestone[];
  facilities: Facility[];
  productIntel: ProductIntel[];
  activeProcedures: number;
  firebaseStatus: {
    us: FirebaseProjectStatus;
    uk: FirebaseProjectStatus;
  };
}

export const missionControlService = {
  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    return apiClient.get<DashboardData>('/mission-control/dashboard');
  },

  async getFacilityConfigs(): Promise<ApiResponse<FacilityConfig[]>> {
    return apiClient.get<FacilityConfig[]>('/mission-control/facilities/config');
  },

  async updateFacilityType(id: string, facilityType: string): Promise<ApiResponse<FacilityConfig>> {
    return apiClient.patch<FacilityConfig>(`/mission-control/facilities/config/${id}`, {
      facilityType,
    });
  },

  async syncFacilities(): Promise<ApiResponse<FacilityConfig[]>> {
    return apiClient.post<FacilityConfig[]>('/mission-control/facilities/sync');
  },

  async getMilestoneConfigs(): Promise<ApiResponse<MilestoneConfig[]>> {
    return apiClient.get<MilestoneConfig[]>('/mission-control/milestones/config');
  },

  async updateMilestoneConfig(
    id: string,
    data: { target?: number; weight?: number; manualCurrent?: number | null }
  ): Promise<ApiResponse<MilestoneConfig>> {
    return apiClient.patch<MilestoneConfig>(`/mission-control/milestones/config/${id}`, data);
  },

  async getProductConfigs(): Promise<ApiResponse<ProductConfig[]>> {
    return apiClient.get<ProductConfig[]>('/mission-control/products/config');
  },

  async updateProductConfig(
    id: string,
    data: { name?: string; platform?: string }
  ): Promise<ApiResponse<ProductConfig>> {
    return apiClient.patch<ProductConfig>(`/mission-control/products/config/${id}`, data);
  },

  async updateProductIntelItem(
    id: string,
    data: { label?: string; value?: string }
  ): Promise<ApiResponse<ProductIntelItemConfig>> {
    return apiClient.patch<ProductIntelItemConfig>(`/mission-control/products/items/${id}`, data);
  },

  async addProductIntelItem(
    productConfigId: string,
    data: { label: string; value: string; sortOrder: number }
  ): Promise<ApiResponse<ProductIntelItemConfig>> {
    return apiClient.post<ProductIntelItemConfig>(
      `/mission-control/products/config/${productConfigId}/items`,
      data
    );
  },

  async deleteProductIntelItem(id: string): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/mission-control/products/items/${id}`);
  },
};
